import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import {
  analyzeMealProject,
  assignMealIncident,
  discardMealIncident,
  exportMealClosure,
  exportMealIncidents,
  getMealIncident,
  getMealIncidentEvents,
  getMealIncidents,
  getMealProjects,
  getMealSummary,
  revalidateMealIncident,
  startMealIncidentReview,
  validateMealIncident,
} from "./mealIncidenciasApi";
import {
  MEAL_INITIAL_FILTERS,
  buildMealClosureExportParams,
  buildMealControlExportParams,
  buildMealIncidentParams,
  canAccessMealIncidents,
  downloadMealExport,
  getMealErrorMessage,
  getMealExportErrorMessage,
  getMealProjectId,
  getMealProjectLabel,
  getMealTypeOptions,
  getStoredMealUser,
  hasMealFilters,
  normalizeMealAnalysisResult,
  normalizeMealExportResponse,
  normalizeMealPagination,
} from "./mealIncidenciasUtils";
import MealReviewSummary from "./components/MealReviewSummary";
import MealIncidentFilters from "./components/MealIncidentFilters";
import MealIncidentTable from "./components/MealIncidentTable";
import MealIncidentDetail from "./components/MealIncidentDetail";
import MealProjectAnalysis from "./components/MealProjectAnalysis";
import MealProjectExports from "./components/MealProjectExports";

const PAGE_SIZE = 20;

export const MealIncidentsRouteGuard = ({ children }) =>
  canAccessMealIncidents(getStoredMealUser()) ? children : <Navigate to="/accessDenied" replace />;

const normalizeIncident = (incident = {}) => ({
  ...incident,
  id: incident.id ?? incident.incidentId ?? incident.idIncidencia,
  type: incident.type ?? incident.incidentType ?? incident.tipo,
  priority: incident.priority ?? incident.prioridad,
  status: incident.status ?? incident.estado,
  conditionActive: incident.conditionActive ?? incident.condicionActiva ?? false,
  affectedRecords: Array.isArray(incident.affectedRecords)
    ? incident.affectedRecords.length
    : incident.affectedRecords ?? incident.recordsCount ?? incident.registrosAfectados ?? 0,
  assignedToName:
    incident.assignedToName ?? incident.assigneeName ?? incident.responsableNombre ?? "",
  detectedAt: incident.detectedAt ?? incident.fechaDeteccion,
  updatedAt: incident.updatedAt ?? incident.fechaActualizacion,
});

const normalizeIncidentDetail = (payload = {}, history = []) => {
  const incident = payload.incident || payload.incidencia || payload;
  const detailHistory = payload.history || payload.eventos || incident.history || [];
  return normalizeIncident({
    ...incident,
    records: payload.records || payload.registros || incident.records || [],
    history: detailHistory.length > 0 ? detailHistory : history,
    rule: payload.rule || payload.regla || incident.rule || {},
    allowedActions: payload.allowedActions || incident.allowedActions || [],
    allowedResolutionTypes:
      payload.allowedResolutionTypes || incident.allowedResolutionTypes || [],
  });
};

const actionSuccessMessages = {
  ASSIGN: "Incidencia asignada correctamente.",
  START_REVIEW: "Revisión iniciada.",
  REVALIDATE: "Revalidación completada.",
  VALIDATE: "Incidencia validada correctamente.",
  DISCARD: "Incidencia descartada.",
};

const MealIncidencias = () => {
  const currentUser = useMemo(() => getStoredMealUser(), []);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [projectsError, setProjectsError] = useState("");

  const [summary, setSummary] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState("");

  const [incidents, setIncidents] = useState([]);
  const [pagination, setPagination] = useState(normalizeMealPagination({}, PAGE_SIZE));
  const [filters, setFilters] = useState({ ...MEAL_INITIAL_FILTERS });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedIncidentId, setSelectedIncidentId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [feedback, setFeedback] = useState({ message: "", severity: "success" });
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [controlExportLoading, setControlExportLoading] = useState(false);
  const [closureExportLoading, setClosureExportLoading] = useState(false);

  const projectsSequence = useRef(0);
  const summarySequence = useRef(0);
  const listSequence = useRef(0);
  const detailSequence = useRef(0);

  const projectId = getMealProjectId(selectedProject);

  const loadProjects = useCallback(async () => {
    const requestId = ++projectsSequence.current;
    setLoadingProjects(true);
    setProjectsError("");
    try {
      const data = await getMealProjects();
      if (requestId !== projectsSequence.current) return;
      setProjects(data);
      setSelectedProject((current) => {
        if (current && data.some((project) => String(getMealProjectId(project)) === String(getMealProjectId(current)))) {
          return current;
        }
        return data.length === 1 ? data[0] : null;
      });
    } catch (error) {
      if (requestId !== projectsSequence.current) return;
      setProjects([]);
      setSelectedProject(null);
      setProjectsError(getMealErrorMessage(error));
    } finally {
      if (requestId === projectsSequence.current) setLoadingProjects(false);
    }
  }, []);

  const loadSummary = useCallback(async (activeProjectId) => {
    if (!activeProjectId) return;
    const requestId = ++summarySequence.current;
    setLoadingSummary(true);
    setSummaryError("");
    try {
      const data = await getMealSummary(activeProjectId);
      if (requestId === summarySequence.current) setSummary(data || {});
    } catch (error) {
      if (requestId === summarySequence.current) {
        setSummary({});
        setSummaryError(getMealErrorMessage(error));
      }
    } finally {
      if (requestId === summarySequence.current) setLoadingSummary(false);
    }
  }, []);

  const loadList = useCallback(async (activeProjectId, activeFilters, activePage, activePageSize) => {
    if (!activeProjectId) return;
    if (activeFilters.dateFrom && activeFilters.dateTo && activeFilters.dateFrom > activeFilters.dateTo) {
      setListError("La fecha desde no puede ser posterior a la fecha hasta.");
      setIncidents([]);
      return;
    }

    const requestId = ++listSequence.current;
    setLoadingList(true);
    setListError("");
    try {
      const result = await getMealIncidents(buildMealIncidentParams({
        projectId: activeProjectId,
        filters: activeFilters,
        page: activePage,
        pageSize: activePageSize,
      }));
      if (requestId !== listSequence.current) return;
      setIncidents(result.incidents.map(normalizeIncident));
      setPagination(normalizeMealPagination(result.pagination, activePageSize));
    } catch (error) {
      if (requestId !== listSequence.current) return;
      setIncidents([]);
      setPagination(normalizeMealPagination({ page: activePage, pageSize: activePageSize }, activePageSize));
      setListError(getMealErrorMessage(error));
    } finally {
      if (requestId === listSequence.current) setLoadingList(false);
    }
  }, []);

  const loadDetail = useCallback(async (incidentId) => {
    if (!incidentId) return;
    const requestId = ++detailSequence.current;
    setLoadingDetail(true);
    setDetailError("");
    try {
      const [detailResult, eventsResult] = await Promise.allSettled([
        getMealIncident(incidentId),
        getMealIncidentEvents(incidentId),
      ]);
      if (requestId !== detailSequence.current) return;
      if (detailResult.status === "rejected") throw detailResult.reason;
      const history = eventsResult.status === "fulfilled" ? eventsResult.value : [];
      setDetail(normalizeIncidentDetail(detailResult.value, history));
    } catch (error) {
      if (requestId === detailSequence.current) {
        setDetail(null);
        setDetailError(getMealErrorMessage(error));
      }
    } finally {
      if (requestId === detailSequence.current) setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
    return () => {
      projectsSequence.current += 1;
      summarySequence.current += 1;
      listSequence.current += 1;
      detailSequence.current += 1;
    };
  }, [loadProjects]);

  useEffect(() => {
    setSummary({});
    setIncidents([]);
    if (projectId) {
      loadSummary(projectId);
    } else {
      summarySequence.current += 1;
      listSequence.current += 1;
      setLoadingSummary(false);
      setLoadingList(false);
    }
  }, [projectId, loadSummary]);

  useEffect(() => {
    if (projectId) loadList(projectId, filters, page, pageSize);
  }, [projectId, filters, page, pageSize, loadList]);

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handleProjectChange = (value) => {
    setPage(1);
    setFilters({ ...MEAL_INITIAL_FILTERS });
    setSelectedProject(value);
    setAnalysisResult(null);
  };

  const handleOpenIncident = (incident) => {
    setSelectedIncidentId(incident.id);
    setDetail(null);
    setDetailOpen(true);
    loadDetail(incident.id);
  };

  const handleCloseDetail = () => {
    detailSequence.current += 1;
    setDetailOpen(false);
    setSelectedIncidentId(null);
    setDetail(null);
    setDetailError("");
  };

  const refreshVisibleData = useCallback(async () => {
    if (!projectId) return;
    await Promise.all([
      loadSummary(projectId),
      loadList(projectId, filters, page, pageSize),
      selectedIncidentId ? loadDetail(selectedIncidentId) : Promise.resolve(),
    ]);
  }, [filters, loadDetail, loadList, loadSummary, page, pageSize, projectId, selectedIncidentId]);

  const handleAnalyzeProject = async () => {
    if (!projectId || analysisLoading) return false;
    setAnalysisLoading(true);
    try {
      const result = normalizeMealAnalysisResult(await analyzeMealProject(projectId));
      setAnalysisResult(result);
      setFeedback({
        message: result.created > 0
          ? "Análisis completado y cola actualizada."
          : "Análisis actualizado. No aparecieron nuevas incidencias.",
        severity: "success",
      });
      await refreshVisibleData();
      return true;
    } catch (error) {
      setFeedback({ message: getMealErrorMessage(error), severity: "error" });
      return false;
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleExportControl = async () => {
    if (!projectId || controlExportLoading) return;
    setControlExportLoading(true);
    try {
      const response = await exportMealIncidents(
        buildMealControlExportParams({ projectId, filters })
      );
      downloadMealExport(normalizeMealExportResponse(response, { kind: "control", projectId }));
      setFeedback({ message: "Control de incidencias descargado correctamente.", severity: "success" });
    } catch (error) {
      setFeedback({ message: await getMealExportErrorMessage(error), severity: "error" });
    } finally {
      setControlExportLoading(false);
    }
  };

  const handleExportClosure = async () => {
    if (!projectId || closureExportLoading) return;
    setClosureExportLoading(true);
    try {
      const response = await exportMealClosure(
        buildMealClosureExportParams({ projectId, filters })
      );
      downloadMealExport(normalizeMealExportResponse(response, { kind: "closure", projectId }));
      setFeedback({ message: "Base revisada descargada correctamente.", severity: "success" });
    } catch (error) {
      setFeedback({ message: await getMealExportErrorMessage(error), severity: "error" });
    } finally {
      setClosureExportLoading(false);
    }
  };

  const handleAction = async (action, values = {}) => {
    if (!selectedIncidentId || actionLoading) return false;
    setActionLoading(action);
    try {
      if (action === "ASSIGN") await assignMealIncident(selectedIncidentId, values.assignedTo);
      if (action === "START_REVIEW") await startMealIncidentReview(selectedIncidentId);
      if (action === "REVALIDATE") await revalidateMealIncident(selectedIncidentId);
      if (action === "VALIDATE") await validateMealIncident(selectedIncidentId, values);
      if (action === "DISCARD") await discardMealIncident(selectedIncidentId, values);

      setFeedback({ message: actionSuccessMessages[action], severity: "success" });
      await refreshVisibleData();
      return true;
    } catch (error) {
      setFeedback({ message: getMealErrorMessage(error), severity: "error" });
      return false;
    } finally {
      setActionLoading("");
    }
  };

  const typeOptions = useMemo(
    () => getMealTypeOptions(incidents, summary),
    [incidents, summary]
  );
  const emptyMessage = hasMealFilters(filters)
    ? "No hay incidencias que coincidan con los filtros seleccionados."
    : "No hay incidencias para este proyecto.";

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "flex-start" }} gap={2} mb={3}>
        <Box>
          <Typography variant="overline" color="primary" fontWeight={900}>MEAL</Typography>
          <Typography variant="h3" fontWeight={900}>Revisión de incidencias</Typography>
          <Typography color="text.secondary">Control y validación de calidad de datos</Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshOutlinedIcon />}
          onClick={refreshVisibleData}
          disabled={!projectId || loadingSummary || loadingList}
        >
          Actualizar
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <FactCheckOutlinedIcon color="primary" />
          <Typography variant="h5" fontWeight={900}>Proyecto de revisión</Typography>
        </Stack>
        {projectsError && (
          <Alert severity="error" action={<Button color="inherit" onClick={loadProjects}>Reintentar</Button>} sx={{ mb: 2 }}>
            {projectsError}
          </Alert>
        )}
        {loadingProjects ? (
          <Box minHeight={70} display="flex" alignItems="center"><CircularProgress size={28} aria-label="Cargando proyectos MEAL" /></Box>
        ) : projects.length === 0 && !projectsError ? (
          <Alert severity="info">No tienes proyectos MEAL disponibles.</Alert>
        ) : (
          <Autocomplete
            options={projects}
            value={selectedProject}
            onChange={(event, value) => handleProjectChange(value)}
            getOptionLabel={getMealProjectLabel}
            isOptionEqualToValue={(option, value) => String(getMealProjectId(option)) === String(getMealProjectId(value))}
            disableClearable={projects.length === 1}
            disabled={projects.length === 1 || analysisLoading}
            sx={{ maxWidth: 640 }}
            renderInput={(params) => <TextField {...params} label="Proyecto" placeholder="Seleccionar proyecto" />}
          />
        )}
        <Box mt={2}>
          <Typography variant="subtitle2" fontWeight={900} mb={1}>
            Acciones MEAL
          </Typography>
          <MealProjectAnalysis
            projectName={selectedProject ? getMealProjectLabel(selectedProject) : ""}
            projectSelected={Boolean(projectId)}
            loading={analysisLoading}
            result={analysisResult}
            onAnalyze={handleAnalyzeProject}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <MealProjectExports
          projectSelected={Boolean(projectId)}
          controlLoading={controlExportLoading}
          closureLoading={closureExportLoading}
          onExportControl={handleExportControl}
          onExportClosure={handleExportClosure}
        />
      </Paper>

      {selectedProject && (
        <Stack spacing={2}>
          {summaryError && <Alert severity="error">{summaryError}</Alert>}
          <MealReviewSummary summary={summary} loading={loadingSummary} />
          <MealIncidentFilters
            filters={filters}
            typeOptions={typeOptions}
            onChange={handleFilterChange}
            onClear={() => { setPage(1); setFilters({ ...MEAL_INITIAL_FILTERS }); }}
            disabled={loadingList}
          />
          <MealIncidentTable
            incidents={incidents}
            loading={loadingList}
            error={listError}
            emptyMessage={emptyMessage}
            pagination={pagination}
            onPageChange={setPage}
            onPageSizeChange={(value) => { setPage(1); setPageSize(value); }}
            onOpen={handleOpenIncident}
            onRetry={() => loadList(projectId, filters, page, pageSize)}
          />
        </Stack>
      )}

      <MealIncidentDetail
        open={detailOpen}
        loading={loadingDetail}
        error={detailError}
        incident={detail}
        history={detail?.history || []}
        currentUser={currentUser}
        loadingAction={actionLoading}
        onClose={handleCloseDetail}
        onRetry={() => loadDetail(selectedIncidentId)}
        onAction={handleAction}
      />

      <Snackbar
        open={Boolean(feedback.message)}
        autoHideDuration={5000}
        onClose={() => setFeedback({ message: "", severity: "success" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={feedback.severity} onClose={() => setFeedback({ message: "", severity: "success" })}>
          {feedback.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MealIncidencias;
